/**
 * Own the colour mobile browsers paint their chrome with.
 *
 * iOS Safari tints the strip above the page — the one the clock sits in —
 * from `<meta name="theme-color">` where it finds one, and otherwise by
 * sampling whatever the page is painting at its top edge. Sampled or not, it
 * latches: a red destructive toast crossing the top edge once leaves a red
 * band there long after the toast is gone.
 *
 * Keeping the meta current is the answer to both halves. On a browser that
 * reads it there is nothing to sample in the first place; on one that samples
 * anyway, the toast is held clear of the edge (see components/ui/toast.tsx)
 * so there is nothing red to find.
 *
 * The leaf startup screen is the one thing the page paints that isn't the
 * canvas, so it is the one thing that has to say so.
 */

/** Leaf green: SplashScreen.backgroundColor in capacitor.config.ts. */
const SPLASH_GROUND = "#3E5539";

/**
 * Whatever index.html shipped — read once, before anything can change it, so
 * "back to normal" means the site's own colour rather than a second opinion
 * about which cream is the right one.
 */
const DEFAULT_GROUND = readThemeColor();

export type ThemeColorGround = "splash" | "app";

function themeColorMeta(): HTMLMetaElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector('meta[name="theme-color"]');
}

function readThemeColor(): string | null {
  return themeColorMeta()?.getAttribute("content") ?? null;
}

/** Point the browser's chrome at the ground the page is actually painting. */
export function setBrowserThemeColor(ground: ThemeColorGround): void {
  const meta = themeColorMeta();
  if (!meta) return;
  const next = ground === "splash" ? SPLASH_GROUND : DEFAULT_GROUND;
  if (next === null) return; // nothing to restore to — leave it alone
  meta.setAttribute("content", next);
}

export default setBrowserThemeColor;
